import { onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { useQueryClient } from "@tanstack/vue-query";
import { getSocket } from "@/utils/socket";
import {
  GET_TRIAL_STATUS,
  GET_SUBSCRIPTION_STATUS,
  GET_ORGANIZATION_ACTIVE_SUBSCRIPTION,
} from "@/store/action-types";
import { INVALIDATE_SUBSCRIPTION_CACHE, SET_PERSISTENT_BANNER } from "@/store/mutation-types";
import { useToast } from "@/composables/ui/useToast";
import type { Socket } from "socket.io-client";

const BILLING_UPDATE_CHANNEL = "billing:update";

type BillingEvent = {
  event?: string;
  data?: unknown;
  ts?: number;
};

export function useGlobalBillingUpdates() {
  const store = useStore();
  const queryClient = useQueryClient();
  const toast = useToast();
  let socket: Socket | null = null;
  let lastEventTs = 0;

  const invalidateCreditKeys = () => {
    queryClient.invalidateQueries({ queryKey: ["billing", "creditBalance"] });
    queryClient.invalidateQueries({ queryKey: ["billing", "creditHistory"] });
  };

  const invalidateSubscriptionKeys = () => {
    store.commit(INVALIDATE_SUBSCRIPTION_CACHE);
    store.dispatch(GET_TRIAL_STATUS, { forceRefresh: true });
    store.dispatch(GET_SUBSCRIPTION_STATUS, { forceRefresh: true });
    store.dispatch(GET_ORGANIZATION_ACTIVE_SUBSCRIPTION, { forceRefresh: true });
    queryClient.invalidateQueries({ queryKey: ["billing", "subscriptionStatus"] });
    queryClient.invalidateQueries({ queryKey: ["billing", "billingEstimate"] });
    queryClient.invalidateQueries({ queryKey: ["organizationSubscriptionStatus"] });
  };

  const invalidateInvoiceKeys = () => {
    queryClient.invalidateQueries({ queryKey: ["billing", "invoices"] });
    queryClient.invalidateQueries({ queryKey: ["billing", "billingEstimate"] });
  };

  const invalidatePlanCatalog = () => {
    queryClient.invalidateQueries({ queryKey: ["billing", "planCatalog"] });
  };

  const handleBillingUpdate = (payload?: BillingEvent) => {
    const event = payload?.event;
    const ts = payload?.ts;
    if (typeof ts === "number" && ts <= lastEventTs) {
      console.debug("[billing:update] dropping out-of-order event", event, ts);
      return;
    }
    if (typeof ts === "number") lastEventTs = ts;

    switch (event) {
      case "credits_purchased":
        toast.success("Credits purchased");
        invalidateCreditKeys();
        break;
      case "credits_refunded":
        toast.info("Credits refunded");
        invalidateCreditKeys();
        break;
      case "payment_disputed":
        store.commit(SET_PERSISTENT_BANNER, {
          type: "error",
          message:
            "A payment dispute has been opened on your account. Please contact support to resolve it.",
          dismissible: true,
        });
        break;
      case "subscription_created":
        toast.success("Subscription created");
        invalidateSubscriptionKeys();
        break;
      case "subscription_updated":
        toast.info("Subscription updated");
        invalidateSubscriptionKeys();
        break;
      case "plan_changed":
        toast.info("Plan changed");
        invalidateSubscriptionKeys();
        break;
      case "subscription_canceled":
        toast.warning("Subscription canceled");
        invalidateSubscriptionKeys();
        break;
      case "downgrade_cancelled":
        toast.info("Scheduled downgrade cancelled");
        invalidateSubscriptionKeys();
        break;
      case "invoice_paid":
        toast.success("Invoice paid");
        invalidateInvoiceKeys();
        invalidateSubscriptionKeys();
        break;
      case "invoice_failed":
        toast.error("Invoice payment failed");
        invalidateInvoiceKeys();
        invalidateSubscriptionKeys();
        break;
      case "charge_failed":
        toast.error("Charge failed");
        invalidateInvoiceKeys();
        invalidateSubscriptionKeys();
        break;
      case "plan_catalog_updated":
        invalidatePlanCatalog();
        break;
      default:
        console.debug("[billing:update] unknown event", event, payload);
        invalidateSubscriptionKeys();
        break;
    }
  };

  onMounted(() => {
    socket = getSocket();
    socket.on(BILLING_UPDATE_CHANNEL, handleBillingUpdate);
  });

  onUnmounted(() => {
    socket?.off(BILLING_UPDATE_CHANNEL, handleBillingUpdate);
  });
}
